import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { ChartDraftCanaryConfig } from 'src/app/models/chart-draft-canary.model';
import { Patient } from 'src/app/models/patient.model';
import { NewAuthService } from '../auth/new-auth-service.service';
import { FirestoreService } from '../firestore.service';

/**
 * Gates the chart-draft / preserve-work-on-call feature behind a canary allowlist so it only runs
 * for a targeted group of users. Reads {@link ChartDraftCanaryConfig} from
 * `welby_configuration/featureToggles` in real time and cross-references the authenticated user, so
 * the group can be changed (or the feature killed) from Firestore without a deploy.
 *
 * Fails closed: if the config is missing or the user is not yet resolved, the feature stays off, and
 * an empty allowlist reaches nobody rather than everybody. Reaching everyone is a separate, explicit
 * opt-in (`allowAll: true`), which is also what retires the canary without a deploy — the alternative
 * being an allowlist that has to name every practice and goes stale the moment a new one is onboarded.
 */
@Injectable({
  providedIn: 'root',
})
export class ChartDraftCanaryService {
  private static readonly CONFIG_PATH = 'welby_configuration/featureToggles';
  private static readonly FEATURE_KEY = 'chartDraftCanary';

  /** Latest evaluated value, cached for synchronous guards (e.g. imperative service methods). */
  private enabledCache = false;

  /**
   * Emits whether the feature is enabled for the current user; multicast + replayed.
   *
   * The user side is derived from `isLoggedObservable$` (a BehaviorSubject that emits on every
   * login/logout) rather than `user$` — the latter is only assigned on interactive login, not on
   * session restore, so it can't be relied on. Re-evaluating on the login flag also resets the
   * flag on logout (→ `null` user → disabled), so a login/logout on a shared workstation never
   * inherits the previous user's evaluation. `catchError` on the config read fails closed if the
   * listener errors (e.g. `permission-denied` during an auth-token transition).
   */
  readonly isEnabled$: Observable<boolean> = combineLatest([
    this.firestore.doc$(ChartDraftCanaryService.CONFIG_PATH).pipe(catchError(() => of(null))),
    this.auth.isLoggedObservable$.pipe(map((isLoggedIn) => (isLoggedIn ? this.auth.user : null))),
  ]).pipe(
    map(([config, user]) => this.evaluate(config?.[ChartDraftCanaryService.FEATURE_KEY], user)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(private firestore: FirestoreService, private auth: NewAuthService) {
    // Keep the synchronous snapshot warm for imperative guards.
    this.isEnabled$.subscribe((enabled) => (this.enabledCache = enabled));
  }

  /** Synchronous snapshot of the canary state (fails closed until the config resolves). */
  get isEnabled(): boolean {
    return this.enabledCache;
  }

  /**
   * True when the feature is enabled for this user. Fails closed: requires `enabled` + an
   * authenticated user, then only `allowAll: true` (explicit opt-in) reaches everyone. Otherwise the
   * user must be listed by id, or their practice by `client_responsible_id` — an empty, omitted or
   * malformed allowlist resolves to nobody, so a misconfigured *allowlist* can only withhold the
   * feature, never widen it. `allowAll` is the one field that widens, which is why it is matched
   * strictly: only the boolean `true` opens the feature up.
   */
  private evaluate(config: ChartDraftCanaryConfig | undefined, user: Patient | undefined): boolean {
    try {
      // Strict on `enabled` for the same reason as `allowAll` below, and with more at stake: this is
      // the kill switch, and a truthy non-boolean would silently disarm it. The string "false" typed
      // in the Firestore console is truthy, so a loose check would leave the feature ON for the
      // allowlisted — or, with `allowAll`, for everyone — exactly when someone meant to kill it.
      if (config?.enabled !== true || !user) {
        return false;
      }
      // Strict equality on purpose, unlike the sibling canaries: this doc is hand-edited in the
      // Firestore console, where the field's type is picked by hand, and a truthy non-boolean
      // (the string "false" being the obvious trap) would otherwise open the feature org-wide.
      if (config.allowAll === true) {
        return true;
      }
      const byUser = !!user.user_id && this.toList(config.userIds).includes(user.user_id);
      const byClient = !!user.client_responsible_id && this.toList(config.clientIds).includes(user.client_responsible_id);
      return byUser || byClient;
    } catch {
      // Fails closed: any unexpected/malformed config can do no more than withhold the feature, never
      // error the `isEnabled$` stream (which would freeze `enabledCache` at its last value for this
      // root singleton — and this doc is hand-edited in Firestore, so malformed shapes do happen).
      return false;
    }
  }

  /** Guards against a hand-edited doc holding something other than an array (a string, a number, null). */
  private toList(value: string[] | undefined): string[] {
    return Array.isArray(value) ? value : [];
  }
}
